import { formatUserCurrency } from '../lib/money';
/**
 * Report Service - API calls for generating and downloading reports
 * 
 * Backend endpoints:
 * GET /api/admin/reports/orders?startDate=&endDate=  - Get orders report data
 * GET /api/admin/reports/users?startDate=&endDate=   - Get users report data
 */

import api from './api';
import { getMethodName, getInstrumentName } from '../lib/paymentLabels';

/**
 * Safely format a date value
 * Returns formatted date string or 'N/A' if invalid
 */
const formatDate = (dateValue, format = 'datetime') => {
  if (!dateValue) return 'N/A';
  try {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'N/A';
    
    if (format === 'date') {
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Properly escape a value for CSV format
 * - Handles null, undefined, objects, arrays
 * - Wraps in quotes if contains comma, quote, or newline
 * - Escapes embedded quotes by doubling them
 */
const escapeCSVValue = (value) => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return '';
  }
  
  // Handle objects and arrays - convert to readable string
  if (typeof value === 'object') {
    if (value instanceof Date) {
      return formatDate(value);
    }
    try {
      // For address objects or other complex data
      if (Array.isArray(value)) {
        return escapeCSVValue(value.join(', '));
      }
      // Try to extract meaningful data from object
      const str = JSON.stringify(value);
      return escapeCSVValue(str === '{}' ? '' : str);
    } catch {
      return '';
    }
  }
  
  const stringValue = String(value);
  
  // Skip invalid strings
  if (stringValue === 'undefined' || stringValue === 'null' || stringValue === '[object Object]') {
    return '';
  }
  
  // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
    return `"${stringValue.replaceAll('"', '""')}"`;
  }
  return stringValue;
};

const REPORT_ENDPOINTS = {
  ORDERS: '/admin/reports/orders',
  USERS: '/admin/reports/users',
  TOTAL_SALES: '/admin/reports/total-sales',
  BMF_COINS: '/admin/reports/bmf-coins',
  REFUNDS: '/admin/reports/refunds',
  ANALYTICS: '/admin/reports/analytics'
};

const reportService = {
  /**
   * Generic helper method to fetch report data from API endpoints
   * @param {string} endpoint - API endpoint path
   * @param {string} startDate - ISO date string
   * @param {string} endDate - ISO date string
   * @param {string} defaultErrorMessage - Fallback error message
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async _getReport(endpoint, startDate, endDate, defaultErrorMessage) {
    try {
      const response = await api.get(endpoint, {
        params: { startDate, endDate }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || defaultErrorMessage
      };
    }
  },

  /**
   * Generic helper method to construct CSV content and trigger download
   * @param {Array<string>} headers - Header row array
   * @param {Array<Array>} rows - Data rows array
   * @param {Array<Array>} summaryRows - Summary rows array
   * @param {string} filename - Output CSV filename
   */
  _generateCSV(headers, rows, summaryRows = [], filename) {
    const csvContent = [
      headers.map(escapeCSVValue).join(','),
      ...rows.map(row => row.join(',')),
      ...summaryRows.map(row => row.join(','))
    ].join('\n');

    this._downloadCSV(csvContent, filename);
  },

  /**
   * Get orders report data for a date range
   * @param {string} startDate - ISO date string
   * @param {string} endDate - ISO date string
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async getOrdersReport(startDate, endDate) {
    return this._getReport(REPORT_ENDPOINTS.ORDERS, startDate, endDate, 'Failed to fetch orders report');
  },

  /**
   * Get users report data for a date range
   * @param {string} startDate - ISO date string
   * @param {string} endDate - ISO date string
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async getUsersReport(startDate, endDate) {
    return this._getReport(REPORT_ENDPOINTS.USERS, startDate, endDate, 'Failed to fetch users report');
  },

  /**
   * Get total sales report data for a date range
   */
  async getTotalSalesReport(startDate, endDate) {
    return this._getReport(REPORT_ENDPOINTS.TOTAL_SALES, startDate, endDate, 'Failed to fetch total sales report');
  },

  /**
   * Get BMF Coins report data for a date range
   */
  async getBMFCoinsReport(startDate, endDate) {
    return this._getReport(REPORT_ENDPOINTS.BMF_COINS, startDate, endDate, 'Failed to fetch BMF Coins report');
  },

  /**
   * Get monetary refunds report data for a date range
   */
  async getRefundsReport(startDate, endDate) {
    return this._getReport(REPORT_ENDPOINTS.REFUNDS, startDate, endDate, 'Failed to fetch refunds report');
  },

  /**
   * Convert orders data to CSV and trigger download
   * @param {Array} orders - Array of order objects
   * @param {Object} summary - Summary statistics
   * @param {string} filename - Output filename
   */
  downloadOrdersCSV(orders, summary, filename = 'orders-sales-report.csv') {
    const headers = [
      'Order ID',
      'Order Date & Time',
      // BMF-146. Same position as the preview table, so the export and the
      // screen stay column-for-column.
      'Delivery Date',
      'Customer Name',
      'Delivery Agent',
      'Order Value (Subtotal) (₹)',
      'Discount (₹)',
      'Delivery Charges (₹)',
      // BMF-144. Placed between the delivery charge and the GST, matching the
      // preview table, so the export and the screen stay column-for-column.
      'Total Order Value (₹)',
      'Taxable Amount (₹)',
      'GST Amount (₹)',
      'Net Paid (₹)',
      'BMF Coins / Wallet Paid (₹)',
      'Payment Method',
      // The ticket's wording for the instrument; the field it comes from keeps
      // its own name (order.paymentInstrument).
      'Payment Mode',
      'Order Status'
    ];

    const rows = orders.map(order => [
      escapeCSVValue(order.orderNumber || order.id),
      escapeCSVValue(formatDate(order.createdAt)),
      // 'date' not 'datetime': the column is a date, and formatDate already
      // yields 'N/A' when the order has no delivery timestamp.
      escapeCSVValue(formatDate(order.deliveredAt, 'date')),
      escapeCSVValue(order.customerName),
      escapeCSVValue(order.agentName || 'Unassigned'),
      escapeCSVValue(order.productSubtotal || 0),
      escapeCSVValue(order.discountAmount || 0),
      escapeCSVValue(order.deliveryFee || 0),
      // Two decimals like the GST beside them: a spreadsheet reconciling
      // Taxable + GST against Total Order Value needs the paise present.
      escapeCSVValue(Number(order.totalOrderValue || 0).toFixed(2)),
      escapeCSVValue(Number(order.taxableAmount || 0).toFixed(2)),
      escapeCSVValue(Number(order.gstAmount || 0).toFixed(2)),
      escapeCSVValue((Number(order.cashAmount || 0) + Number(order.onlineAmount || 0))),
      escapeCSVValue(order.walletCoinsAmount || 0),
      // Named through the shared labels, so the export and the screen it was
      // taken from never word the same payment differently. Two columns rather
      // than one "COD (UPI)" string, so the sheet can group by either.
      escapeCSVValue(order.paymentMethod ? getMethodName(order.paymentMethod) : 'N/A'),
      escapeCSVValue(getInstrumentName(order.paymentInstrument) || 'N/A'),
      escapeCSVValue(order.status)
    ]);

    const summaryRows = [
      [],
      [escapeCSVValue('--- SUMMARY ---')],
      [escapeCSVValue('Total Number of Orders'), escapeCSVValue(summary.totalOrders)],
      [escapeCSVValue('Gross Sales Amount (₹)'), escapeCSVValue(summary.grossSalesAmount || 0)],
      [escapeCSVValue('Total Discounts / Savings (₹)'), escapeCSVValue(summary.totalDiscounts || 0)],
      [escapeCSVValue('Total Delivery Charges Collected (₹)'), escapeCSVValue(summary.totalDeliveryCharges || 0)],
      [escapeCSVValue('Total GST Collected (₹)'), escapeCSVValue(Number(summary.totalGST || 0).toFixed(2))],
      // Cash only. A COD order collected on the UPI QR is counted as online
      // revenue, because that money reached the bank rather than the agent.
      [escapeCSVValue('Cash Revenue (₹)'), escapeCSVValue(summary.cashRevenue || 0)],
      [escapeCSVValue('Online Revenue (₹)'), escapeCSVValue(summary.onlineRevenue || 0)],
      [escapeCSVValue('BMF Coins Spent (₹)'), escapeCSVValue(summary.bmfCoinsRedeemed || 0)],
      [escapeCSVValue('Net Sales Amount (₹)'), escapeCSVValue(summary.netSalesAmount || summary.totalRevenue || 0)],
      [escapeCSVValue('Date Range'), escapeCSVValue(`${summary.dateRange?.startDate || 'All'} to ${summary.dateRange?.endDate || 'All'}`)]
    ];

    this._generateCSV(headers, rows, summaryRows, filename);
  },

  /**
   * Convert users data to CSV and trigger download
   * @param {Array} users - Array of user objects
   * @param {Object} summary - Summary statistics
   * @param {string} filename - Output filename
   */
  downloadUsersCSV(users, summary, filename = 'users-report.csv') {
    const headers = [
      'User ID',
      'Name',
      'Email',
      'Phone',
      'Role',
      'Status',
      'Last Login',
      'Registration Date',
      'Orders',
      'Total Spent',
      'Last Order Date'
    ];
    const rows = users.map(user => [
      user.id,
      user.name || '',
      user.email,
      user.phone || 'N/A',
      user.role,
      user.status,
      formatDate(user.lastLogin),
      formatDate(user.createdAt, 'date'),
      user.ordersCount ?? 0,
      formatUserCurrency(user.totalSpent ?? 0),
      user.lastOrderDate ? formatDate(user.lastOrderDate, 'date') : ''
    ].map(escapeCSVValue));

    this._generateCSV(headers, rows, [], filename);
  },

  /**
   * Get analytics report data for a date range
   * @param {string} startDate - ISO date string
   * @param {string} endDate - ISO date string
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async getAnalyticsReport(startDate, endDate) {
    return this._getReport(REPORT_ENDPOINTS.ANALYTICS, startDate, endDate, 'Failed to fetch analytics report');
  },

  /**
   * Convert analytics data to CSV and trigger download
   * @param {Object} data - Analytics report data
   * @param {string} filename - Output filename
   */
  downloadAnalyticsCSV(data, filename = 'analytics-report.csv') {
    const { dailyRevenue, topProducts, statusDistribution, paymentDistribution, summary } = data;

    // Summary section
    const summarySection = [
      [escapeCSVValue('--- ANALYTICS REPORT ---')],
      [],
      [escapeCSVValue('Date Range'), escapeCSVValue(`${summary.dateRange.startDate || 'All'} to ${summary.dateRange.endDate || 'All'}`)],
      [],
      [escapeCSVValue('--- SUMMARY ---')],
      [escapeCSVValue('Total Orders'), escapeCSVValue(summary.totalOrders)],
      [escapeCSVValue('Total Revenue (₹)'), escapeCSVValue(summary.totalRevenue)],
      [escapeCSVValue('Average Order Value (₹)'), escapeCSVValue(Math.round(summary.avgOrderValue))],
      [escapeCSVValue('Paid Orders'), escapeCSVValue(summary.paidOrders)],
      [escapeCSVValue('Paid Revenue (₹)'), escapeCSVValue(summary.paidRevenue)],
    ];

    // Daily Revenue section
    const dailyRevenueSection = [
      [],
      [escapeCSVValue('--- DAILY REVENUE ---')],
      [escapeCSVValue('Date'), escapeCSVValue('Revenue (₹)'), escapeCSVValue('Orders')],
      ...dailyRevenue.map(d => [
        escapeCSVValue(d.date),
        escapeCSVValue(d.revenue),
        escapeCSVValue(d.orders)
      ])
    ];

    // Top Products section
    const topProductsSection = [
      [],
      [escapeCSVValue('--- TOP SELLING PRODUCTS ---')],
      [escapeCSVValue('Rank'), escapeCSVValue('Product Name'), escapeCSVValue('Quantity Sold'), escapeCSVValue('Revenue (₹)')],
      ...topProducts.map((p, idx) => [
        escapeCSVValue(idx + 1),
        escapeCSVValue(p.productName),
        escapeCSVValue(p.totalQuantity),
        escapeCSVValue(p.totalRevenue)
      ])
    ];

    // Order Status Distribution section
    const statusSection = [
      [],
      [escapeCSVValue('--- ORDER STATUS DISTRIBUTION ---')],
      [escapeCSVValue('Status'), escapeCSVValue('Count'), escapeCSVValue('Revenue (₹)')],
      ...statusDistribution.map(s => [
        escapeCSVValue(s.status),
        escapeCSVValue(s.count),
        escapeCSVValue(s.revenue)
      ])
    ];

    // Payment Status Distribution section
    const paymentSection = [
      [],
      [escapeCSVValue('--- PAYMENT STATUS DISTRIBUTION ---')],
      [escapeCSVValue('Payment Status'), escapeCSVValue('Count'), escapeCSVValue('Revenue (₹)')],
      ...paymentDistribution.map(p => [
        escapeCSVValue(p.paymentStatus),
        escapeCSVValue(p.count),
        escapeCSVValue(p.revenue)
      ])
    ];

    const csvContent = [
      ...summarySection,
      ...dailyRevenueSection,
      ...topProductsSection,
      ...statusSection,
      ...paymentSection
    ].map(row => row.join(',')).join('\n');

    this._downloadCSV(csvContent, filename);
  },

  /**
   * Convert total sales data to CSV and trigger download
   */
  downloadTotalSalesCSV(orders, summary, filename = 'total-sales-report.csv') {
    const headers = [
      'Order ID',
      'Order Date & Time',
      // BMF-146. This report is not filtered to delivered orders, so this
      // column is legitimately 'N/A' for anything still in flight.
      'Delivery Date',
      'Customer Name',
      'Order Value (Subtotal) (₹)',
      'Discount / Flash Savings (₹)',
      'Delivery Charges (₹)',
      'GST Amount (₹)',
      'Final Amount Paid (₹)',
      'BMF Coins / Wallet Paid (₹)',
      'Payment Method',
      'Order Status'
    ];

    const rows = orders.map(order => [
      escapeCSVValue(order.orderNumber || order.id),
      escapeCSVValue(formatDate(order.createdAt)),
      escapeCSVValue(formatDate(order.deliveredAt, 'date')),
      escapeCSVValue(order.customerName),
      escapeCSVValue(order.productSubtotal || 0),
      escapeCSVValue(order.discountAmount || 0),
      escapeCSVValue(order.deliveryFee || 0),
      escapeCSVValue(Number(order.gstAmount || 0).toFixed(2)),
      escapeCSVValue(order.totalAmount || 0),
      escapeCSVValue(order.walletCoinsAmount || order.walletAmount || 0),
      escapeCSVValue(order.paymentMethod ? getMethodName(order.paymentMethod) : 'N/A'),
      escapeCSVValue(order.status)
    ]);

    const summaryRows = [
      [],
      [escapeCSVValue('--- SUMMARY ---')],
      [escapeCSVValue('Total Number of Orders'), escapeCSVValue(summary.totalOrders)],
      [escapeCSVValue('Gross Sales Amount (₹)'), escapeCSVValue(summary.grossSalesAmount)],
      [escapeCSVValue('Total Discount / Flash Savings (₹)'), escapeCSVValue(summary.totalDiscounts || 0)],
      [escapeCSVValue('Total Delivery Charges Collected (₹)'), escapeCSVValue(summary.totalDeliveryCharges)],
      [escapeCSVValue('Total GST Collected (₹)'), escapeCSVValue(summary.totalGST.toFixed(2))],
      [escapeCSVValue('Total BMF Coins / Wallet Paid (₹)'), escapeCSVValue(summary.totalWalletPaid || summary.bmfCoinsRedeemed || 0)],
      [escapeCSVValue('Net Sales Amount (₹)'), escapeCSVValue(summary.netSalesAmount)],
      [escapeCSVValue('Date Range'), escapeCSVValue(`${summary.dateRange?.startDate || 'All'} to ${summary.dateRange?.endDate || 'All'}`)]
    ];

    this._generateCSV(headers, rows, summaryRows, filename);
  },

  /**
   * Convert BMF Coins data to CSV and trigger download
   */
  downloadBMFCoinsCSV(transactions, summary, filename = 'bmf-coins-report.csv') {
    const headers = [
      'User Name',
      'Mobile Number/User ID',
      'Coins Credited',
      'Credit Date',
      'Coins Redeemed',
      'Redemption Date',
      'Type',
      'Category',
      'Transaction Ref ID',
      'Description'
    ];

    const rows = transactions.map(t => [
      escapeCSVValue(t.userName),
      escapeCSVValue(`${t.phone} / #${t.userId}`),
      escapeCSVValue(t.creditedAmount !== null && t.creditedAmount !== undefined ? t.creditedAmount : ''),
      escapeCSVValue(t.creditDate ? formatDate(t.creditDate) : ''),
      escapeCSVValue(t.redeemedAmount !== null && t.redeemedAmount !== undefined ? t.redeemedAmount : ''),
      escapeCSVValue(t.redemptionDate ? formatDate(t.redemptionDate) : ''),
      escapeCSVValue(t.type || ''),
      escapeCSVValue(t.category || ''),
      escapeCSVValue(t.transactionRefId && t.transactionRefId !== 'NONE' ? t.transactionRefId : ''),
      escapeCSVValue(t.description || '')
    ]);

    const summaryRows = [
      [],
      [escapeCSVValue('--- SUMMARY ---')],
      [escapeCSVValue('Total BMF Coins Credited'), escapeCSVValue(summary.totalCredited)],
      [escapeCSVValue('Total BMF Coins Redeemed/Used'), escapeCSVValue(summary.totalRedeemed)],
      [escapeCSVValue('Date Range'), escapeCSVValue(`${summary.dateRange.startDate || 'All'} to ${summary.dateRange.endDate || 'All'}`)]
    ];

    this._generateCSV(headers, rows, summaryRows, filename);
  },

  /**
   * Convert refunds data to CSV and trigger download
   * @param {Array} refunds - Array of refund objects
   * @param {Object} summary - Summary statistics
   * @param {string} filename - Output filename
   */
  downloadRefundsCSV(refunds, summary, filename = 'refunds-report.csv') {
    const headers = [
      'Order ID',
      'Customer Name',
      'Date of Refund',
      'Amount of Refund (₹)',
      'Refund Status',
      'Reason / Note',
      'Razorpay Refund ID'
    ];

    const rows = refunds.map(r => [
      escapeCSVValue(r.orderNumber || r.orderId),
      escapeCSVValue(r.customerName),
      escapeCSVValue(formatDate(r.refundDate)),
      escapeCSVValue(Number(r.amount || 0).toFixed(2)),
      escapeCSVValue(r.status),
      escapeCSVValue(r.reason || 'Monetary Bank Refund'),
      escapeCSVValue(r.razorpayRefundId || 'N/A')
    ]);

    const summaryRows = [
      [],
      [escapeCSVValue('--- SUMMARY ---')],
      [escapeCSVValue('Total Monetary Refunds Count'), escapeCSVValue(summary.totalRefunds || 0)],
      [escapeCSVValue('Total Refunded Amount (₹)'), escapeCSVValue(Number(summary.totalRefundAmount || 0).toFixed(2))],
      [escapeCSVValue('Date Range'), escapeCSVValue(`${summary.dateRange?.startDate || 'All'} to ${summary.dateRange?.endDate || 'All'}`)]
    ];

    this._generateCSV(headers, rows, summaryRows, filename);
  },

  /**
   * Internal helper to trigger CSV download
   * Adds BOM for proper Excel/spreadsheet encoding
   */
  _downloadCSV(csvContent, filename) {
    // Add BOM for UTF-8 encoding (ensures Excel opens with correct encoding for special characters like ₹)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }
};

export default reportService;